import { useState, useEffect } from 'react';
import { contentAPI, chapterAPI, mcqAPI, classAPI, subjectAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import {
  RiAddLine, RiDeleteBinLine, RiLinksLine, RiToggleLine, RiToggleFill,
  RiFileTextLine, RiFileLine, RiVideoLine, RiQuestionLine, RiSlideshowLine,
  RiLoaderLine, RiEditLine, RiCheckLine, RiDownloadLine, RiExternalLinkLine,
  RiFolderOpenLine,
} from 'react-icons/ri';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const CONTENT_TYPES = [
  { id: 'content',   label: 'Content Files',  icon: RiFileLine,       desc: 'PDFs, documents' },
  { id: 'reference', label: 'References',      icon: RiLinksLine,       desc: 'External links' },
  { id: 'video',     label: 'Videos',          icon: RiVideoLine,      desc: 'YouTube, etc.' },
  { id: 'question',  label: 'Question Papers', icon: RiFileTextLine,   desc: 'Exam papers' },
  { id: 'mcq',       label: 'MCQ Tests',       icon: RiQuestionLine,   desc: 'MCQ links' },
  { id: 'slides',    label: 'Slides',          icon: RiSlideshowLine,  desc: 'Presentations' },
];

const FILE_TYPES = ['content', 'question', 'slides'];
const LINK_TYPES = ['reference', 'video', 'mcq'];

const emptyForm = { title: '', description: '', link: '', questionCount: '', mcqId: '', file: null };

const ContentManagement = () => {
  const [classes, setClasses]     = useState([]);
  const [subjects, setSubjects]   = useState([]);
  const [chapters, setChapters]   = useState([]);
  const [mcqs, setMcqs]           = useState([]);
  const [contents, setContents]   = useState([]);

  const [selectedClass, setSelectedClass]     = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');

  const [activeTab, setActiveTab]         = useState('content');
  const [showForm, setShowForm]           = useState(false);
  const [form, setForm]                   = useState(emptyForm);
  const [loading, setLoading]             = useState(false);
  const [fetchingContent, setFetchingContent] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [initialLoading, setInitialLoading] = useState(true);

  const [mcqDetails, setMcqDetails] = useState({ title: '', description: '', duration: 30, passingPercentage: 40 });
  const [questions, setQuestions]   = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    questionText: '',
    options: [
      { optionLetter: 'A', optionText: '' }, { optionLetter: 'B', optionText: '' },
      { optionLetter: 'C', optionText: '' }, { optionLetter: 'D', optionText: '' },
    ],
    correctAnswer: 'A',
  });
  const [showQuestionForm, setShowQuestionForm]       = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [classRes, chapRes, mcqRes, subRes] = await Promise.all([
          classAPI.getAllClasses(), chapterAPI.getAllChapters(),
          mcqAPI.getAllMCQs(), subjectAPI.getAllSubjects(),
        ]);
        setClasses(classRes.data.classes || []);
        setChapters(chapRes.data.chapters || []);
        setMcqs(mcqRes.data.mcqs || []);
        setSubjects(subRes.data.subjects || []);
      } catch {
        toast.error('Failed to load initial data');
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitial();
  }, []);

  useEffect(() => {
    if (!selectedChapter) { setContents([]); return; }
    setFetchingContent(true);
    contentAPI.getChapterContents(selectedChapter)
      .then(res => setContents(res.data.contents || []))
      .catch(() => toast.error('Failed to load contents'))
      .finally(() => setFetchingContent(false));
  }, [selectedChapter]);

  const filteredSubjects = selectedClass
    ? subjects.filter(s => s.classId?._id === selectedClass || s.classId === selectedClass)
    : [];
  const filteredChapters = selectedSubject
    ? chapters.filter(c => c.subjectId?._id === selectedSubject || c.subjectId === selectedSubject)
    : [];

  const selectedChapterData = chapters.find(c => c._id === selectedChapter);
  const selectedSubjectData = subjects.find(s => s._id === selectedSubject);
  const selectedClassData   = classes.find(c => c._id === selectedClass);
  const tabContents = contents.filter(c => c.type === activeTab);

  const handleClassChange   = (v) => { setSelectedClass(v); setSelectedSubject(''); setSelectedChapter(''); };
  const handleSubjectChange = (v) => { setSelectedSubject(v); setSelectedChapter(''); };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!selectedChapter) { toast.warning('Please select a chapter first'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('chapterId', selectedChapter);
      fd.append('type', activeTab);
      fd.append('title', form.title);
      if (form.description)  fd.append('description', form.description);
      if (form.link)         fd.append('link', form.link);
      if (form.mcqId)        fd.append('mcqId', form.mcqId);
      if (form.questionCount) fd.append('questionCount', form.questionCount);
      if (form.file)         fd.append('file', form.file);
      const res = await contentAPI.addContent(fd);
      setContents(prev => [...prev, res.data.content]);
      setForm(emptyForm);
      setShowForm(false);
      toast.success('Content added successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this content item?')) return;
    try {
      await contentAPI.deleteContent(id);
      setContents(prev => prev.filter(c => c._id !== id));
      toast.success('Deleted successfully');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleMcqDetailsChange = (e) => {
    const { name, value } = e.target;
    setMcqDetails(prev => ({ ...prev, [name]: (name === 'duration' || name === 'passingPercentage') ? parseInt(value) : value }));
  };

  const handleOptionChange = (index, value) => {
    const opts = [...currentQuestion.options];
    opts[index].optionText = value;
    setCurrentQuestion(prev => ({ ...prev, options: opts }));
  };

  const handleAddUpdateQuestion = () => {
    if (!currentQuestion.questionText.trim()) { toast.warning('Please enter question text'); return; }
    if (currentQuestion.options.some(o => !o.optionText.trim())) { toast.warning('Please fill all 4 options'); return; }
    if (editingQuestionIndex !== null) {
      const updated = [...questions];
      updated[editingQuestionIndex] = { ...currentQuestion, questionNumber: editingQuestionIndex + 1 };
      setQuestions(updated);
      setEditingQuestionIndex(null);
    } else {
      setQuestions(prev => [...prev, { ...currentQuestion, questionNumber: prev.length + 1 }]);
    }
    setCurrentQuestion({ questionText: '', options: [{ optionLetter: 'A', optionText: '' }, { optionLetter: 'B', optionText: '' }, { optionLetter: 'C', optionText: '' }, { optionLetter: 'D', optionText: '' }], correctAnswer: 'A' });
    setShowQuestionForm(false);
  };

  const handleEditQuestion = (index) => { setCurrentQuestion(questions[index]); setEditingQuestionIndex(index); setShowQuestionForm(true); };
  const handleDeleteQuestion = (index) => {
    const updated = questions.filter((_, i) => i !== index);
    updated.forEach((q, i) => { q.questionNumber = i + 1; });
    setQuestions(updated);
  };

  const handleMcqSubmit = async (e) => {
    e.preventDefault();
    if (!mcqDetails.title.trim()) { toast.warning('Please enter MCQ title'); return; }
    if (questions.length === 0)   { toast.warning('Please add at least one question'); return; }
    setLoading(true);
    try {
      const mcqRes = await mcqAPI.createMCQ({ ...mcqDetails, chapterId: selectedChapter, questions });
      const newMcq = mcqRes.data.mcq;
      const fd = new FormData();
      fd.append('chapterId', selectedChapter);
      fd.append('type', 'mcq');
      fd.append('title', newMcq.title);
      fd.append('description', `MCQ Test: ${newMcq.description || newMcq.title}`);
      fd.append('mcqId', newMcq._id);
      const contentRes = await contentAPI.addContent(fd);
      setContents(prev => [...prev, contentRes.data.content]);
      setMcqDetails({ title: '', description: '', duration: 30, passingPercentage: 40 });
      setQuestions([]);
      setCurrentQuestion({ questionText: '', options: [{ optionLetter: 'A', optionText: '' }, { optionLetter: 'B', optionText: '' }, { optionLetter: 'C', optionText: '' }, { optionLetter: 'D', optionText: '' }], correctAnswer: 'A' });
      setShowQuestionForm(false);
      setShowForm(false);
      const mcqsRes = await mcqAPI.getAllMCQs();
      setMcqs(mcqsRes.data.mcqs || []);
      toast.success('MCQ Test created and linked successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create MCQ');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyShareLink = (shareCode) => {
    navigator.clipboard.writeText(`${window.location.origin}/mcq/${shareCode}`);
    toast.success('Share link copied to clipboard');
  };

  const handleTogglePublishMcq = async (mcqId) => {
    setMcqs(prev => prev.map(m => m._id === mcqId ? { ...m, isPublished: !m.isPublished } : m));
    try {
      await mcqAPI.togglePublish(mcqId);
    } catch {
      setMcqs(prev => prev.map(m => m._id === mcqId ? { ...m, isPublished: !m.isPublished } : m));
      toast.error('Failed to update MCQ status');
    }
  };

  const handleDeleteMcq = async (mcqId) => {
    if (!window.confirm('Delete this MCQ test? This will also remove it from content.')) return;
    setLoading(true);
    try {
      await mcqAPI.deleteMCQ(mcqId);
      setContents(prev => prev.filter(c => c.mcqId !== mcqId));
      setMcqs(prev => prev.filter(m => m._id !== mcqId));
      toast.success('MCQ deleted successfully');
    } catch {
      toast.error('Failed to delete MCQ');
    } finally {
      setLoading(false);
    }
  };

  const fileUrl = (filePath) => {
    if (!filePath) return null;
    const name = filePath.replace(/\\/g, '/').split('/').pop();
    return `${API_BASE}/uploads/${name}`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
      {/* Breadcrumb selector */}
      <div className="card p-5 mb-5">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Select Content Location</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label">Class</label>
            <select className="input" value={selectedClass} onChange={e => handleClassChange(e.target.value)}>
              <option value="">Select class</option>
              {classes.map(cls => <option key={cls._id} value={cls._id}>{cls.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Subject</label>
            <select className="input" value={selectedSubject} onChange={e => handleSubjectChange(e.target.value)} disabled={!selectedClass}>
              <option value="">Select subject</option>
              {filteredSubjects.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Chapter</label>
            <select className="input" value={selectedChapter} onChange={e => setSelectedChapter(e.target.value)} disabled={!selectedSubject}>
              <option value="">Select chapter</option>
              {filteredChapters.map(ch => (
                <option key={ch._id} value={ch._id}>{ch.chapterNumber ? `Ch.${ch.chapterNumber} – ` : ''}{ch.name}</option>
              ))}
            </select>
          </div>
        </div>
        {selectedChapter && selectedChapterData && (
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
            <RiFolderOpenLine className="w-3.5 h-3.5 text-blue-500" />
            <span className="font-medium text-blue-700">
              {selectedClassData?.name} › {selectedSubjectData?.name} › {selectedChapterData.name}
            </span>
          </div>
        )}
      </div>

      {!selectedChapter ? (
        <div className="card p-14 text-center">
          <RiFolderOpenLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Select a chapter above to manage its content</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-slate-200 overflow-x-auto">
            <div className="flex min-w-max">
              {CONTENT_TYPES.map(t => {
                const count = contents.filter(c => c.type === t.id).length;
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => { setActiveTab(t.id); setShowForm(false); setForm(emptyForm); }}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                      activeTab === t.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {t.label}
                    {count > 0 && (
                      <span className="badge badge-blue py-0 px-1.5">{count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-5">
            {/* Tab header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">{CONTENT_TYPES.find(t => t.id === activeTab)?.desc}</p>
              <button
                onClick={() => {
                  if (activeTab === 'mcq') {
                    setShowForm(v => !v);
                    if (showForm) { setMcqDetails({ title: '', description: '', duration: 30, passingPercentage: 40 }); setQuestions([]); setShowQuestionForm(false); }
                  } else {
                    setShowForm(v => !v);
                    setForm(emptyForm);
                  }
                }}
                className={showForm ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'}
              >
                {showForm ? 'Cancel' : <><RiAddLine className="w-3.5 h-3.5" /> Add</>}
              </button>
            </div>

            {/* MCQ creation form */}
            {activeTab === 'mcq' && showForm && (
              <form onSubmit={handleMcqSubmit} className="card p-5 mb-5 border-l-4 border-violet-500 animate-fade-in-up">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Create MCQ Test</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="label">Title *</label>
                    <input className="input" type="text" name="title" value={mcqDetails.title} onChange={handleMcqDetailsChange} placeholder="e.g. Chapter 1 Quiz" />
                  </div>
                  <div>
                    <label className="label">Chapter</label>
                    <input className="input bg-slate-50 text-slate-500 cursor-not-allowed" disabled value={selectedChapterData?.name || ''} />
                  </div>
                  <div>
                    <label className="label">Duration (minutes)</label>
                    <input className="input" type="number" name="duration" value={mcqDetails.duration} onChange={handleMcqDetailsChange} min="1" />
                  </div>
                  <div>
                    <label className="label">Passing Percentage</label>
                    <input className="input" type="number" name="passingPercentage" value={mcqDetails.passingPercentage} onChange={handleMcqDetailsChange} min="0" max="100" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label">Description</label>
                    <textarea className="input resize-none" name="description" value={mcqDetails.description} onChange={handleMcqDetailsChange} rows={2} placeholder="Optional" />
                  </div>
                </div>

                {/* Questions */}
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-slate-700">Questions <span className="text-blue-600">({questions.length})</span></p>
                    {!showQuestionForm && (
                      <button type="button" onClick={() => setShowQuestionForm(true)} className="btn-success btn-sm">
                        <RiAddLine className="w-3.5 h-3.5" /> Add Question
                      </button>
                    )}
                  </div>

                  {showQuestionForm && (
                    <div className="card p-4 mb-3 border-l-4 border-blue-400 animate-fade-in-up">
                      <label className="label">Question Text *</label>
                      <textarea className="input resize-none mb-3" rows={2} value={currentQuestion.questionText}
                        onChange={e => setCurrentQuestion(p => ({ ...p, questionText: e.target.value }))} placeholder="Enter the question…" />
                      <label className="label">Options</label>
                      <div className="space-y-2 mb-3">
                        {currentQuestion.options.map((opt, idx) => (
                          <div key={idx} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${currentQuestion.correctAnswer === opt.optionLetter ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}>
                            <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                              <input type="radio" name="correctAnswer" value={opt.optionLetter}
                                checked={currentQuestion.correctAnswer === opt.optionLetter}
                                onChange={e => setCurrentQuestion(p => ({ ...p, correctAnswer: e.target.value }))}
                                className="w-4 h-4 accent-emerald-600" />
                              <span className="text-xs font-bold text-slate-500 w-4">{opt.optionLetter}</span>
                            </label>
                            <input type="text" value={opt.optionText} onChange={e => handleOptionChange(idx, e.target.value)}
                              className="flex-1 text-sm bg-transparent border-none outline-none text-slate-700 placeholder-slate-400"
                              placeholder={`Option ${opt.optionLetter}`} />
                            {currentQuestion.correctAnswer === opt.optionLetter && <RiCheckLine className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={handleAddUpdateQuestion} className="btn-primary flex-1 btn-sm">
                          {editingQuestionIndex !== null ? 'Update' : 'Add'} Question
                        </button>
                        <button type="button" onClick={() => { setShowQuestionForm(false); setEditingQuestionIndex(null); setCurrentQuestion({ questionText: '', options: [{ optionLetter: 'A', optionText: '' }, { optionLetter: 'B', optionText: '' }, { optionLetter: 'C', optionText: '' }, { optionLetter: 'D', optionText: '' }], correctAnswer: 'A' }); }} className="btn-secondary flex-1 btn-sm">Cancel</button>
                      </div>
                    </div>
                  )}

                  {questions.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {questions.map((q, idx) => (
                        <div key={idx} className="card p-3 group">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-slate-700 flex-1"><span className="text-blue-600 font-bold">Q{q.questionNumber}.</span> {q.questionText}</p>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                              <button type="button" onClick={() => handleEditQuestion(idx)} className="btn-icon btn-ghost text-blue-600 hover:bg-blue-50"><RiEditLine className="w-3.5 h-3.5" /></button>
                              <button type="button" onClick={() => handleDeleteQuestion(idx)} className="btn-icon btn-ghost text-red-600 hover:bg-red-50"><RiDeleteBinLine className="w-3.5 h-3.5" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {questions.length === 0 && !showQuestionForm && (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center text-slate-400 text-sm mb-3">
                      No questions yet. Add your first question above.
                    </div>
                  )}

                  <button type="submit" disabled={loading || questions.length === 0} className="btn-success w-full">
                    {loading ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Creating…</> : 'Create & Link MCQ Test'}
                  </button>
                </div>
              </form>
            )}

            {/* Regular content add form */}
            {activeTab !== 'mcq' && showForm && (
              <form onSubmit={handleAdd} className="card p-5 mb-5 border-l-4 border-blue-500 animate-fade-in-up space-y-3">
                <h3 className="text-sm font-semibold text-slate-700">Add {CONTENT_TYPES.find(t => t.id === activeTab)?.label}</h3>
                <div>
                  <label className="label">Title *</label>
                  <input className="input" type="text" placeholder="Enter title" value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })} required />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea className="input resize-none" placeholder="Optional description" value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
                </div>
                {FILE_TYPES.includes(activeTab) && (
                  <div>
                    <label className="label">{activeTab === 'slides' ? 'Upload Slides (PDF, PPT, PPTX)' : 'Upload File (PDF, DOC, DOCX)'}</label>
                    <input type="file" accept={activeTab === 'slides' ? '.pdf,.ppt,.pptx' : '.pdf,.doc,.docx'}
                      onChange={e => setForm({ ...form, file: e.target.files[0] || null })}
                      className="w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:font-semibold hover:file:bg-blue-100 cursor-pointer" />
                  </div>
                )}
                {LINK_TYPES.includes(activeTab) && activeTab !== 'mcq' && (
                  <div>
                    <label className="label">URL *</label>
                    <input className="input" type="url" placeholder={activeTab === 'video' ? 'YouTube or video URL' : 'External link URL'}
                      value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} required />
                  </div>
                )}
                {activeTab === 'question' && (
                  <div>
                    <label className="label">Number of Questions</label>
                    <input className="input" type="number" placeholder="Optional" value={form.questionCount}
                      onChange={e => setForm({ ...form, questionCount: e.target.value })} min="1" />
                  </div>
                )}
                <button type="submit" disabled={loading} className="btn-primary w-full">
                  {loading ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Uploading…</> : 'Save Content'}
                </button>
              </form>
            )}

            {/* Content list */}
            {fetchingContent ? (
              <div className="flex justify-center py-10">
                <div className="w-7 h-7 spinner" />
              </div>
            ) : tabContents.length === 0 ? (
              <div className="py-10 text-center">
                {(() => { const Icon = CONTENT_TYPES.find(t => t.id === activeTab)?.icon || RiFileLine; return <Icon className="w-8 h-8 text-slate-300 mx-auto mb-2" />; })()}
                <p className="text-slate-400 text-sm">No {CONTENT_TYPES.find(t => t.id === activeTab)?.label.toLowerCase()} yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tabContents.map(item => (
                  <div key={item._id} className="card-hover p-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate text-sm">{item.title}</p>
                      {item.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{item.description}</p>}
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        {item.filePath && (
                          <a href={fileUrl(item.filePath)} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                            <RiDownloadLine className="w-3 h-3" />
                            {item.fileName || 'Download'}
                            {item.fileSize && <span className="text-slate-400">({formatSize(item.fileSize)})</span>}
                          </a>
                        )}
                        {item.link && (
                          <a href={item.link} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                            <RiExternalLinkLine className="w-3 h-3" /> Open link
                          </a>
                        )}
                        {item.mcqId && (
                          <span className="text-xs text-violet-600 font-medium">
                            MCQ: {typeof item.mcqId === 'object' ? item.mcqId.title : item.mcqId}
                          </span>
                        )}
                        {item.questionCount && (
                          <span className="text-xs text-slate-400">{item.questionCount} questions</span>
                        )}
                      </div>
                    </div>

                    {activeTab === 'mcq' && item.mcqId && (() => {
                      const mcq = typeof item.mcqId === 'object' ? item.mcqId : mcqs.find(m => m._id === item.mcqId);
                      return mcq ? (
                        <div className="flex flex-col gap-1.5 flex-shrink-0 w-32">
                          <button onClick={() => handleCopyShareLink(mcq.shareCode)} className="btn btn-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 text-xs focus:ring-emerald-400 w-full">
                            <RiLinksLine className="w-3 h-3" /> Share
                          </button>
                          <button onClick={() => handleTogglePublishMcq(mcq._id)}
                            className={`btn btn-sm text-xs w-full ${mcq.isPublished ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 focus:ring-amber-400' : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 focus:ring-blue-400'}`}>
                            {mcq.isPublished ? <><RiToggleFill className="w-3 h-3" /> Unpublish</> : <><RiToggleLine className="w-3 h-3" /> Publish</>}
                          </button>
                          <button onClick={() => handleDeleteMcq(mcq._id)} className="btn btn-sm bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 text-xs w-full focus:ring-red-400">
                            <RiDeleteBinLine className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => handleDelete(item._id)} className="btn-icon btn-ghost text-red-600 hover:bg-red-50 flex-shrink-0">
                          <RiDeleteBinLine className="w-4 h-4" />
                        </button>
                      );
                    })()}

                    {activeTab !== 'mcq' && (
                      <button onClick={() => handleDelete(item._id)} className="btn-icon btn-ghost text-red-600 hover:bg-red-50 flex-shrink-0">
                        <RiDeleteBinLine className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
